import { useCallback, useEffect, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { apiCall } from "./api-client.js";
import { ApiError } from "./api-types.js";
import { authAtom } from "./auth-store.js";

/**
 * Phase C.2 共用 fetch hook：统一 loading / error / data state + AbortController 取消。
 * 内部读 authAtom 注入 token。null path → 跳过 fetch（等 :code 等参数就位）。
 *
 * deps 数组变化 → 重 fetch（约定：caller 把 path 关联的所有 deps 都列出来，e.g. orgCode）。
 *
 * `revalidate` = 后台静默重拉：请求期间不置 loading、不清 error、不清 data，只在拿到结果时替换
 * （成功 → 新 data 且清 error；失败 → 新 error，data 保留）。给「定时重试直到恢复」这类调用方用，
 * 免得每次重试都让页面闪一下「加载中」、或在请求飞行期间丢掉上一次的失败结论。
 *
 * 用法：
 *   const { data, loading, error, refetch } = useApiQuery<UsersListResponse>(
 *     code ? `/api/organizations/${code}/users` : null,
 *     [code],
 *   );
 */
export interface UseApiQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
  /** 后台静默重拉（见头注释）。 */
  revalidate: () => Promise<void>;
}

export function useApiQuery<T>(
  path: string | null,
  deps: ReadonlyArray<unknown> = [],
): UseApiQueryResult<T> {
  const auth = useAtomValue(authAtom);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<ApiError | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const run = useCallback(async (silent: boolean): Promise<void> => {
    if (!path || !auth) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await apiCall<T>("GET", path, {
        token: auth.token,
        signal: controller.signal,
      });
      if (!controller.signal.aborted) {
        setData(res);
        if (silent) setError(null);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof ApiError) setError(err);
      else setError(new ApiError(0, "client-error", String(err)));
    } finally {
      // 静默重拉也收尾 loading：它可能打断了一次前台请求（abort 后那次不会自己收尾）。
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [path, auth]);
  const fetchOnce = useCallback(() => run(false), [run]);
  const revalidate = useCallback(() => run(true), [run]);

  useEffect(() => {
    void fetchOnce();
    return () => abortRef.current?.abort();
    // path + auth + caller-supplied deps 全部参与
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, auth?.token, ...deps]);

  return { data, loading, error, refetch: fetchOnce, revalidate };
}
